import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import { useFormik } from "formik";
import * as Yup from "yup";

import moment from "moment";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import TimeField from "../../../Components/Common/Timepicker";
import Modal from "Components/Common/Modal";
import DatePicker from "Components/Common/DatePicker";
import InputFieldText from "Components/Common/InputFieldText";

import { usePostMockDrillDetailMutation } from "apis/FireSafety/FireSafetyMockDrill/fireSafetyMockDrill";

import { generateFormData } from "utils";
import getFileExtension from "utils/getFileExtension";

import { DocumentName } from "pages/UtilityConsumers/style";
import { UploadImage } from "../FireSafetyApplicationRequestList/style";

import pdfImg from "assets/images/pdf.png";

const VALID_FORMATS = ["pdf", "doc", "docx", "xls", "xlsx"];
const MAX_SIZE = 10 * 1024 * 1024;

const MockDrillDetailModal = ({ item, isOpen, id, handleClose, t }) => {
  const fileRef = useRef();

  const { nextMockDrillDueOn } = item;

  const [mockDrillDetails, { isLoading: isMockDrillDetailsLoading }] =
    usePostMockDrillDetailMutation();

  const initialValues = useMemo(
    () => ({
      //   dateOfLastDrill: "",
      proposedDrillDate: "",
      time: "",
      scenario: "",
      file: null,
    }),
    []
  );

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      //   dateOfLastDrill: Yup.date().test("dateOfLastDrill", "", function () {
      //     const { dateOfLastDrill } = this.parent;

      //     if (!dateOfLastDrill) {
      //       return this.createError({
      //         message: "Date of last drill is required",
      //       });
      //     }

      //     return true;
      //   }),
      proposedDrillDate: Yup.date()
        .required("Proposed drill date is required")
        .test(
          "max-date",
          `Proposed drill date must be on or before ${moment(
            nextMockDrillDueOn
          ).format("DD-MM-YYYY")}`,
          function (value) {
            if (!value || !nextMockDrillDueOn) return true;

            return moment(value).isSameOrBefore(
              moment(nextMockDrillDueOn),
              "day"
            );
          }
        ),
      time: Yup.date().test("isDrillTimeRequired", "", function () {
        const { time } = this.parent;

        if (!time) {
          return this.createError({
            message: "Drill time is required",
          });
        }
        return true;
      }),
      scenario: Yup.string().test("isScenarioRequired", "", function () {
        const { scenario } = this.parent;

        if (!scenario) {
          return this.createError({
            message: "Scenario is required",
          });
        }
        return true;
      }),
      file: Yup.mixed().test("fileValidation", "", function (file) {
        if (!file) return true;
        const extension = getFileExtension(file.name.toLowerCase());
        if (!VALID_FORMATS.includes(extension)) {
          return this.createError({ message: "Invalid file format" });
        }
        if (file.size > MAX_SIZE) {
          return this.createError({
            message: "File size should be less than 10 MB",
          });
        }
        return true;
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          mockDrillScheduleId: id,
          //   dateOfLastDrill: values.dateOfLastDrill.format("YYYY-MM-DD"),
          proposedDrillDate: values.proposedDrillDate.format("YYYY-MM-DD"),
          time: moment(values.time).format("YYYY-MM-DDTHH:mm:ss"),
          scenario: values.scenario,
          file: values.file,
        };
        const formData = new FormData();
        generateFormData(formData, payload);

        await mockDrillDetails(formData);

        handleClose();
        resetForm();
      } catch (error) {}
    },
  });
  const {
    handleSubmit,
    // values: { dateOfLastDrill },
  } = formik;
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    formik.setFieldValue("file", file);
    fileRef.current.value = "";
  };

  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Modal
      handleClose={handleClose}
      isOpen={isOpen}
      maxWidth="lg"
      title={t("Mock Drill Details")}
    >
      <Grid container spacing={2} className="mt-2">
        {/* <Grid item xs={12} sm={4}>
          <DatePicker
            label={t("Date of Last Drill") + "*"}
            name="dateOfLastDrill"
            formik={formik}
            format="DD-MM-YYYY"
          />
        </Grid> */}

        <Grid item xs={12} sm={4}>
          <DatePicker
            label={t("Proposed Drill Date") + "*"}
            name="proposedDrillDate"
            formik={formik}
            disablePast
            format="DD-MM-YYYY"
            maxDate={moment(nextMockDrillDueOn)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TimeField
            label={t("Drill Time") + "*"}
            name="time"
            formik={formik}
          />
        </Grid>
        
        <Grid item xs={12} sm={12}>
          <InputFieldText
            label={t("Scenario") + "*"}
            name="scenario"
            InputLabelProps={{ shrink: true }}
            formik={formik}
            placeholder="Scenario"
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("Upload Document (Optional)")}
          </Typography>

          <Box className="d-flex gap-3 flex-wrap">
            {formik.values.file ? (
              <div className="image-container">
                <Box
                  component="img"
                  className="image d-block m-auto"
                  alt="document"
                  src={
                    getFileExtension(formik.values.file.name) === "pdf"
                      ? pdfImg
                      : URL.createObjectURL(formik.values.file)
                  }
                />

                <i
                  className="ri-close-circle-line fs-3 close-btn"
                  onClick={handleRemoveFile}
                />

                <Tooltip title={formik.values.file.name}>
                  <DocumentName fontSize={13} variant="body2">
                    {formik.values.file.name}
                  </DocumentName>
                </Tooltip>
              </div>
            ) : (
              <Box
                component="label"
                className="mb-3 p-1 cursor-pointer"
                sx={{ border: "1px dashed black" }}
              >
                <UploadImage>
                  <AddIcon />
                </UploadImage>

                <input
                  ref={fileRef}
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                />
              </Box>
            )}
          </Box>

          {formik.touched.file && formik.errors.file && (
            <Typography color="error" variant="caption">
              {formik.errors.file}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} className="d-flex justify-content-end gap-2 mt-2">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isMockDrillDetailsLoading}
            startIcon={
              isMockDrillDetailsLoading && <CircularProgress size={20} />
            }
          >
            {t("Submit")}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

MockDrillDetailModal.propTypes = {
  item: PropTypes.object,
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(MockDrillDetailModal);
